
import React from 'react'

import AiHero from '../../Components/Automation/AiHero'
import WhyAIAutomation from '../../Components/Automation/WhyAIAutomation'
import TechServicesSection from '../../Components/Automation/TechServicesSection'
import IndustriesWeServe from '../../Components/Automation/IndustriesWeServe'
import WhyPartnerWithUs from '../../Components/Automation/WhyPartnerWithUs'
import Banner from '../../Components/Banner'
import { Helmet } from 'react-helmet-async'

// Generative AI & LLM Features
const featuresData = [
    {
        id: 1,
        title: "Intelligent Content Generation",
        description:
            "Generate relevant text, summaries, reports, and other content with generative AI solutions tailored to your business workflows and communication needs.",
        image: "/assets/career/img10.png",
    },
    {
        id: 2,
        title: "Context-Aware AI Experiences",
        description:
            "Build AI applications that understand prompts, interpret context, and generate useful responses grounded in your business information and requirements.",
        image: "/assets/home-/ai-2.png",
    },
    {
        id: 3,
        title: "Enterprise Knowledge Access",
        description:
            "Make business knowledge easier to access through AI-powered search, document understanding, and retrieval-augmented generation solutions.",
        image: "/assets/home-/ai-3.png",
    },
    {
        id: 4,
        title: "Scalable AI Integration",
        description:
            "Integrate large language models into existing applications and workflows with appropriate security controls, performance optimization, and scalable infrastructure.",
        image: "/assets/home-/ai-4.png",
    },
]

// Generative AI & LLM Service Offerings
const services = [
    {
        id: 1,
        title: "Generative AI Development",
        category: "service",
        desc: "Develop custom generative AI applications that create content, assist users, process information, and support business-specific use cases using modern AI models.",
        defaultBg: "bg-gradient-to-b from-[#01386A] to-[#1C355E]",
    },
    {
        id: 2,
        title: "Large Language Model Integration",
        category: "service",
        desc: "Integrate large language models into business applications to support intelligent text generation, question answering, summarization, and natural language interactions.",
        defaultBg: "bg-gradient-to-r from-[#01386A] to-[#4B6CB7]",
    },
    {
        id: 3,
        title: "Retrieval-Augmented Generation (RAG)",
        category: "service",
        desc: "Build RAG solutions that connect language models with your documents, knowledge bases, and business data to deliver more relevant, context-grounded responses.",
        defaultBg: "bg-gradient-to-br from-[#01386A] to-[#8E9EAB]",
    },
    {
        id: 4,
        title: "Custom LLM Development & Fine-Tuning",
        category: "service",
        desc: "Adapt suitable language models to domain-specific requirements through fine-tuning, prompt optimization, and evaluation based on your business use cases.",
        defaultBg: "bg-gradient-to-bl from-[#01386A] to-[#6A85B6]",
    },
    {
        id: 5,
        title: "AI Copilots & Virtual Assistants",
        category: "service",
        desc: "Create AI-powered assistants that help employees and customers find information, summarize content, complete tasks, and interact with business systems.",
        defaultBg: "bg-gradient-to-tr from-[#01386A] to-[#253B5C]",
    },
    {
        id: 6,
        title: "AI Agents & Workflow Automation",
        category: "service",
        desc: "Develop AI agents that use approved tools and business integrations to support multi-step workflows, retrieve information, and assist with routine operational tasks.",
        defaultBg: "bg-gradient-to-t from-[#01386A] to-[#243B55]",
    },
    {
        id: 7,
        title: "Prompt Engineering & Optimization",
        category: "service",
        desc: "Design and refine prompts, system instructions, and context strategies to improve response quality, consistency, relevance, and model performance.",
        defaultBg: "bg-gradient-to-tl from-[#01386A] to-[#141E30]",
    },
    {
        id: 9,
        title: "LLM Evaluation & Optimization",
        category: "service",
        desc: "Evaluate AI outputs for quality, relevance, reliability, latency, and cost. Optimize model selection and application workflows to meet production requirements.",
        defaultBg: "bg-gradient-to-br from-[#004e92] to-[#01386A]",
    },
]

// Generative AI & LLM Page
function GenerativeAI() {
    return (
        <div>
            <Helmet>
                <title>
                    Generative AI & LLM Development Services | Mobikode
                </title>

                <meta
                    name="description"
                    content="Mobikode delivers Generative AI and LLM development services, including large language model integration, RAG solutions, AI copilots, AI agents, prompt engineering, and enterprise AI applications."
                />

                <meta
                    name="keywords"
                    content="Generative AI Services, Generative AI Development, Large Language Models, LLM Development Services, LLM Integration, Enterprise Generative AI, Retrieval Augmented Generation, RAG Development, AI Copilot Development, AI Agents, LLM Fine Tuning, Prompt Engineering, AI Application Development, LLM Optimization, Enterprise AI Solutions"
                />

                <link
                    rel="canonical"
                    href="https://www.mobikode.com/generative-ai-llm"
                />

                <meta
                    property="og:title"
                    content="Generative AI & LLM Development Services | Mobikode"
                />

                <meta
                    property="og:description"
                    content="Build business-ready generative AI applications with Mobikode. Explore LLM integration, RAG, AI copilots, AI agents, prompt engineering, and enterprise AI solutions."
                />

                <meta
                    property="og:url"
                    content="https://www.mobikode.com/generative-ai-llm"
                />

                <meta
                    property="og:type"
                    content="website"
                />

                <meta
                    property="og:image"
                    content="https://www.mobikode.com/assets/service-detail/bg3.png"
                />

                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content="Generative AI & LLM Development Services | Mobikode"
                />

                <meta
                    name="twitter:description"
                    content="Discover Mobikode's Generative AI and LLM services, including RAG applications, AI copilots, AI agents, model integration, and enterprise AI development."
                />

                <meta
                    name="twitter:image"
                    content="https://www.mobikode.com/assets/service-detail/bg3.png"
                />
            </Helmet>

            {/* Hero Section */}
            <AiHero
                title="Generative AI & LLM"
                text="Intelligence. Creativity. Transformation."
                imgData="/assets/service-detail/bg3.png"
            />

            {/* Introduction Section - Existing UI Preserved */}
            <section className="w-full pt-16 lg:pt-[100px] bg-white overflow-hidden relative">
                <div className="max-w-[1440px] mx-auto px-[15px] lg:px-[80px]">

                    <div className="relative w-full flex flex-col lg:flex-row lg:items-center group lg:h-[507px]">

                        {/* Image Box */}
                        <div className="relative lg:absolute top-0 right-0 w-full lg:max-w-[656px] h-[250px] md:h-[350px] lg:h-full bg-gray-100 overflow-hidden">
                            <img
                                src="/assets/career/img9.jpg"
                                alt="Generative AI and large language model development solutions"
                                className="w-full h-full object-cover mix-blend-multiply transition-transform duration-700 group-hover:scale-105"
                            />
                        </div>

                        {/* Content Box */}
                        <div className="relative z-20 w-[92%] sm:w-[85%] lg:w-full max-w-[719px] mx-auto lg:mx-0 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1)] px-[20px] py-[30px] lg:px-[60px] lg:py-[80px] -mt-[40px] lg:mt-0">

                            <h3 className="text-[#01386A] text-[20px] lg:text-[30px] font-[600] font-sora mb-[15px] lg:mb-[35px] leading-[1.3]">
                                Transforming Business with Generative AI
                            </h3>

                            <p className="text-[#49577A] w-full text-[14px] lg:text-[16px] leading-[1.6] font-graphik font-[500] text-justify">
                                At Mobikode, we help businesses unlock the potential of Generative AI and large language models. From intelligent assistants and AI-powered knowledge systems to custom LLM applications and workflow automation, we develop solutions aligned with real business needs. Our approach combines model integration, contextual data retrieval, and responsible implementation to help organizations improve productivity, access information faster, and build more intelligent digital experiences.
                            </p>

                            {/* Bottom Border Animation */}
                            <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#01386A]"></div>

                            <div className="absolute bottom-0 left-0 h-[4px] bg-[#ED6800] w-0 transition-all duration-500 ease-out group-hover:w-full"></div>

                        </div>
                    </div>
                </div>
            </section>

            {/* Why Generative AI & LLM */}
            <WhyAIAutomation
                title="Why Generative AI & LLM with Mobikode?"
                fData={featuresData}
            />

            {/* Service Offerings */}
            <TechServicesSection
                title="Our Generative AI & LLM Services"
                sData={services}
            />

            {/* Existing UI Components */}
            <IndustriesWeServe />

            <WhyPartnerWithUs />

            {/* Bottom Banner */}
            <Banner
                title="Generative AI & LLM"
                text="Build intelligent AI-powered applications that unlock knowledge, accelerate workflows, and create new possibilities for your business."
            />
        </div>
    )
}

export default GenerativeAI
